/**
 * NotificationBell — TopNav chrome notification surface.
 * Push #82.6 item A frontend.
 *
 * Renders a bell icon button + numeric badge when unread > 0. Click
 * opens a dropdown showing the 20 most recent notifications fetched
 * from /api/me/notifications. Per-row click navigates to action_url
 * + marks the row read; header "Mark all read" affordance does a
 * batch update.
 *
 * Read state is optimistic — click a row, the badge ticks down + the
 * row dims immediately; the server POST settles in the background. If
 * the POST fails (network), the badge resyncs from the next 30s poll.
 *
 * Integration: drop <NotificationBell /> into TopNav's right-cluster
 * between the Workspace label + the Help button. Lives in the
 * .topnav-user wrapper convention so it shares the existing icon
 * sizing/spacing rhythm. CSS class `.topnav-bell` for any specific
 * styling overrides (default inherits .topnav-helpbtn shape).
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NotificationRow {
  id: string;
  /** Push 4-1d smoke-fix (2026-07-02): sys__notification.short_id
   *  (ntf-prefix). Fallback drill when action_url is null. */
  short_id: string;
  kind: string;
  title: string;
  body: string | null;
  action_url: string | null;
  severity: 'info' | 'warning' | 'critical' | string;
  read_at: string | null;
  created_at: string;
}

interface FeedResponse {
  unread_count: number;
  notifications: NotificationRow[];
}

export function NotificationBell() {
  const [open, setOpen] = useState<boolean>(false);
  const [feed, setFeed] = useState<FeedResponse>({ unread_count: 0, notifications: [] });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Push 4-1d smoke-fix (2026-07-02): admin shell rows fall back to
  // /admin/notifications/:short_id; user shell rows to /notifications/:short_id.
  const isAdminShell = location.pathname.startsWith('/admin');

  // Push #82.9 — event-driven refresh, no interval polling. Same shape as
  // ChatCreditPill: the bell is "as of your last action" not "live to the
  // millisecond." Refresh on mount, on tab focus, on bell-open (so the
  // dropdown shows fresh content when the user clicks it), and on the
  // custom `olytic:notifications-changed` event (Chat.tsx fires this when
  // a dispatch ends because approval / error / wallet-cap notifications
  // can land server-side as a side effect).
  //
  // Pairs with the ChatCreditPill change. Both pollers were running every
  // 30s from every mounted chat surface, and the combined load showed up
  // in dev-tools as a notifications+wallet storm with one
  // ERR_CONNECTION_CLOSED. Cutting both to event-driven removes the
  // baseline traffic while keeping the UX fresh at the moments it
  // matters.
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (document.visibilityState === 'hidden') return;
      try {
        const r = await fetch('/api/me/notifications', { credentials: 'include' });
        if (!r.ok) return;
        const data = (await r.json()) as FeedResponse;
        if (!cancelled) setFeed(data);
      } catch {
        /* silent — bell just doesn't update */
      }
    }
    void load();
    function onFocus(): void {
      void load();
    }
    function onChanged(): void {
      void load();
    }
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    window.addEventListener('olytic:notifications-changed', onChanged);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('olytic:notifications-changed', onChanged);
    };
  }, []);

  // Push #82.9 — refresh when the dropdown opens. Cheap, covers the
  // "I just got back to this tab and want fresh notifications" case
  // without any timer.
  useEffect(() => {
    if (!open) return;
    void fetch('/api/me/notifications', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data !== null) setFeed(data as FeedResponse);
      })
      .catch(() => {
        /* silent */
      });
  }, [open]);

  // Close dropdown on outside click + Esc.
  useEffect(() => {
    if (!open) return undefined;
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function markRead(id: string): Promise<void> {
    // Optimistic — bump local state immediately.
    setFeed((prev) => ({
      unread_count: Math.max(0, prev.unread_count - 1),
      notifications: prev.notifications.map((n) =>
        n.id === id && n.read_at === null
          ? { ...n, read_at: new Date().toISOString() }
          : n,
      ),
    }));
    try {
      await fetch('/api/me/notifications/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
    } catch {
      /* silent — next poll resyncs */
    }
  }

  async function markAllRead(): Promise<void> {
    setFeed((prev) => ({
      unread_count: 0,
      notifications: prev.notifications.map((n) =>
        n.read_at === null ? { ...n, read_at: new Date().toISOString() } : n,
      ),
    }));
    try {
      await fetch('/api/me/notifications/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ all: true }),
      });
    } catch {
      /* silent */
    }
  }

  function onRowClick(n: NotificationRow): void {
    if (n.read_at === null) void markRead(n.id);
    setOpen(false);
    if (n.action_url !== null && n.action_url !== '') {
      // Same-origin paths get react-router navigate; external URLs window.open.
      if (n.action_url.startsWith('/')) {
        navigate(n.action_url);
      } else {
        window.open(n.action_url, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    // Push 4-1d smoke-fix (2026-07-02): fallback drill to the notification
    // record page when the server didn't provide an action_url. Ensures every
    // row is navigable — the notification detail page shows the linked-record
    // jump chip inside if any deeper drill is warranted.
    if (n.short_id !== undefined && n.short_id !== null && n.short_id !== '') {
      navigate(`${isAdminShell ? '/admin/notifications' : '/notifications'}/${n.short_id}`);
    }
  }

  const unread = feed.unread_count;

  return (
    <div className="topnav-user" ref={containerRef}>
      <button
        type="button"
        className="topnav-helpbtn topnav-bell"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        title={unread > 0 ? `${unread} unread` : 'Notifications'}
        style={{ position: 'relative' }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10 3.2c-2.7 0-4.8 2.2-4.8 4.9v2.5l-1.4 2.2h12.4l-1.4-2.2V8.1c0-2.7-2.1-4.9-4.8-4.9z" />
          <path d="M8.3 14.6a1.8 1.8 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              minWidth: 14,
              height: 14,
              padding: '0 4px',
              borderRadius: 7,
              // Push 5-post-smoke-6 (2026-07-11): --danger token doesn't exist;
              // fallback hex was the actual rendered color. Migrated to
              // canonical --olytic-red.
              background: 'var(--olytic-red)',
              color: 'var(--olytic-white)',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: '14px',
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="topnav-dropdown"
          role="menu"
          style={{ minWidth: 320, maxWidth: 380, maxHeight: 480, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              // Push 5-post-smoke-6 (2026-07-11): --surface-border → --border
              borderBottom: '1px solid var(--border)',
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            <span>Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--olytic-teal)',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>
          <div style={{ overflowY: 'auto', flex: '1 1 auto' }}>
            {feed.notifications.length === 0 ? (
              <div
                className="muted"
                style={{ padding: '32px 16px', fontSize: 13, textAlign: 'center', lineHeight: 1.55 }}
              >
                You're all caught up.
              </div>
            ) : (
              feed.notifications.map((n) => {
                const isUnread = n.read_at === null;
                // Push 5-post-smoke-6 (2026-07-11): --danger token doesn't
                // exist; use canonical --olytic-red. --warning token also
                // doesn't exist; keep as an inline literal ochre until an
                // amber/gold system token lands.
                const tint =
                  n.severity === 'critical'
                    ? 'var(--olytic-red)'
                    : n.severity === 'warning'
                      ? '#d97706'
                      : 'var(--olytic-teal)';
                return (
                  <button
                    type="button"
                    key={n.id}
                    onClick={() => onRowClick(n)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 14px',
                      border: 'none',
                      // Push 5-post-smoke-6 (2026-07-11): --surface-border → --border
                      borderBottom: '1px solid var(--border)',
                      background: isUnread ? 'rgba(20, 184, 166, 0.04)' : 'transparent',
                      cursor: 'pointer',
                      opacity: isUnread ? 1 : 0.7,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span
                        aria-hidden="true"
                        style={{
                          flexShrink: 0,
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: isUnread ? tint : 'transparent',
                          marginTop: 6,
                        }}
                      />
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: isUnread ? 600 : 500, marginBottom: 2 }}>
                          {n.title}
                        </div>
                        {n.body !== null && n.body !== '' && (
                          <div
                            className="muted"
                            style={{
                              fontSize: 12,
                              lineHeight: 1.45,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {n.body}
                          </div>
                        )}
                        <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>
                          {formatRelativeTime(n.created_at)}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
